/* eslint-disable @typescript-eslint/no-explicit-any */

import { TInvite } from '@gen2/api/invites/api';
import {
  InvitesKeys,
  useEditDraftInviteMutation,
} from '@gen2/api/invites/hooks';
import { queryClient } from '@gen2/config';
import { toEndDayOfOrgTimezone } from '@gen2/utils/time';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createEditDraftPayload } from '../../invites/send-invites/helpers';
import { useSendInviteStore } from '../../invites/send-invites/store';

export const useUpdateInviteName = () => {
  const { t } = useTranslation('sendInvite');

  const { mutate: editDraftInviteMutation, isLoading: isUpdating } =
    useEditDraftInviteMutation();

  // state
  const store = useSendInviteStore();

  // router
  const params = useParams<{ id: string }>();
  const inviteId = useMemo(() => {
    // for both new and edit
    return store.invite.id || params.id;
  }, [params.id, store.invite.id]);

  // update invite name
  const onUpdateInviteName = async (values: TInvite) => {
    if (!inviteId) return;

    const newValues = {
      ...values,
      reference: values.reference ?? null,
      from_user_id: values.from_user?.id ?? '',
      due_at: values.due_at ? toEndDayOfOrgTimezone(values.due_at) : null,
    };
    editDraftInviteMutation(createEditDraftPayload(inviteId, newValues), {
      onSuccess: () => {
        store.setBannerMessage({
          severity: 'success',
          message: store.isEditSendInvite
            ? t('notDraftSaved')
            : t('draftSaved'),
        });
        queryClient.invalidateQueries([InvitesKeys.getInvite]);
      },
      onError: (error: unknown) => {
        const err = error as AxiosResponse;
        store.setBannerMessage({
          severity: 'error',
          message: err.data.message,
        });
      },
    });
  };

  return {
    onUpdateInviteName,
    isUpdating,
  };
};
